import { supabase } from '@/integrations/supabase/client';

const REPORT_ISSUE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sim-report-issue`;
const MAX_RECENT_ERRORS = 20;
const AUTO_REPORT_COOLDOWN_MS = 10 * 60 * 1000;

export type IssueSeverity = 'minor' | 'blocking' | 'urgent' | 'system';

export type RecentClientError = {
  message: string;
  stack?: string;
  source?: string;
  context?: Record<string, unknown>;
  timestamp: string;
};

export type IssueAttachmentPayload = {
  name: string;
  mimeType: string;
  sizeBytes: number;
  base64: string;
};

export type IssueScreenshotPayload = {
  name: string;
  dataUrl: string;
  caption?: string;
};

export type IssueReportPayload = {
  source?: 'manual' | 'automatic';
  severity: IssueSeverity;
  title: string;
  description?: string;
  screenshotDataUrl?: string | null;
  screenshots?: IssueScreenshotPayload[];
  attachments?: IssueAttachmentPayload[];
  workflowStep?: string | null;
  diagnostics?: Record<string, unknown>;
  errorMessage?: string;
  errorStack?: string;
};

const recentErrors: RecentClientError[] = [];

function getIssueContext(): Record<string, unknown> {
  return (window as unknown as { meddSimIssueContext?: Record<string, unknown> }).meddSimIssueContext || {};
}

export function recordClientError(error: unknown, context: Record<string, unknown> = {}) {
  const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));
  recentErrors.push({
    message: err.message,
    stack: err.stack,
    source: context.source as string | undefined,
    context,
    timestamp: new Date().toISOString(),
  });
  while (recentErrors.length > MAX_RECENT_ERRORS) recentErrors.shift();
}

export function getRecentClientErrors() {
  return [...recentErrors];
}

function getBrowserInfo() {
  return {
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    online: navigator.onLine,
  };
}

function getViewport() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  };
}

export async function fileToIssueAttachment(file: File): Promise<IssueAttachmentPayload> {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read attachment'));
    reader.readAsDataURL(file);
  });

  return {
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    base64,
  };
}

export async function submitIssueReport(payload: IssueReportPayload): Promise<{ id: string }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('Sign in to send an issue report');

  const context = getIssueContext();
  const response = await fetch(REPORT_ISSUE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    },
    body: JSON.stringify({
      ...payload,
      activeTenantId: typeof context.tenantId === 'string' ? context.tenantId : null,
      activeTenantName: typeof context.tenantName === 'string' ? context.tenantName : null,
      workflowStep: payload.workflowStep ?? context.workflowStep ?? null,
      currentUrl: window.location.href,
      route: window.location.pathname,
      userAgent: navigator.userAgent,
      browserInfo: getBrowserInfo(),
      viewport: getViewport(),
      recentErrors: getRecentClientErrors(),
      diagnostics: {
        ...context,
        ...(payload.diagnostics || {}),
      },
    }),
  });

  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || `Issue report failed: ${response.status}`);
  }

  return data.data;
}

export async function reportSystemIssue(error: unknown, context: Record<string, unknown> = {}) {
  const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));
  recordClientError(err, context);

  const fingerprint = `${window.location.pathname}:${context.source || 'unknown'}:${err.message}`.slice(0, 240);
  const storageKey = `medd_sim_auto_report:${fingerprint}`;
  const lastSent = Number(sessionStorage.getItem(storageKey) || 0);
  if (Date.now() - lastSent < AUTO_REPORT_COOLDOWN_MS) return;
  sessionStorage.setItem(storageKey, String(Date.now()));

  try {
    await submitIssueReport({
      source: 'automatic',
      severity: 'system',
      title: `Automatic error: ${String(context.source || err.name || 'Client error')}`,
      description: 'This issue was captured automatically by the MEDD SIM monitoring layer.',
      errorMessage: err.message,
      errorStack: err.stack,
      diagnostics: context,
    });
    window.dispatchEvent(new CustomEvent('medd:error-reported', {
      detail: {
        area: context.area || context.source || 'app',
        action: context.action || 'automatic_issue_report',
        severity: 'error',
      },
    }));
  } catch (reportError) {
    console.warn('Automatic issue report failed:', reportError);
  }
}
